import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import type { Call } from "@twilio/voice-sdk";

export interface IncomingCallMeta {
    fromNumber: string;
    callSid: string | null;
    /** Matched lead (if any) — populated asynchronously after lookup. */
    leadId?: string | null;
    leadName?: string | null;
}

/**
 * Caller is parked in a Twilio Conference, waiting for the user to accept.
 * Populated by useInboundCallWatcher from the realtime `calls` feed.
 */
export interface PendingInbound {
    callId: string;
    fromNumber: string;
    leadId: string | null;
    leadName: string | null;
    conferenceName: string;
    /** Server-side ringing-since timestamp (ISO). */
    startedAt: string;
    /** Seconds of unanswered ringing before auto-declining to voicemail. */
    ringSeconds?: number;
}

interface VoiceCallState {
    /** Server-side ringing call (caller in conference). Drives InboundCallCard. */
    pendingInbound: PendingInbound | null;
    /** Legacy: set if Twilio Voice SDK direct-dispatches an incoming. */
    incoming: { call: Call; meta: IncomingCallMeta } | null;
    /** Connected call — outbound dialer or post-accept conference join. */
    active: { call: Call; meta: IncomingCallMeta } | null;
    muted: boolean;
}

/**
 * Bridge so the InboundCallCard (rendered inside the provider) can fire an
 * outbound `device.connect()` without importing the Voice SDK Device ref —
 * useVoiceDevice owns the ref and registers an implementation here on mount.
 *
 * Accepts the call's display metadata (caller phone, matched lead, etc.) so
 * the resulting `active` state surfaces caller info in ActiveCallBar and
 * lets the user click into the lead sheet during the live call.
 */
type ConnectToConferenceFn = (
    conferenceName: string,
    meta?: IncomingCallMeta,
) => Promise<Call | null>;

interface VoiceCallContextValue extends VoiceCallState {
    setPendingInbound: (v: PendingInbound | null) => void;
    setIncoming: (v: { call: Call; meta: IncomingCallMeta } | null) => void;
    setActive: (v: { call: Call; meta: IncomingCallMeta } | null) => void;
    promoteToActive: () => void;
    clearActive: () => void;
    setMuted: (m: boolean) => void;
    patchIncomingMeta: (patch: Partial<IncomingCallMeta>) => void;
    /** Replace the connect-to-conference implementation (called by useVoiceDevice). */
    registerConnectToConference: (fn: ConnectToConferenceFn | null) => void;
    /** Invoked by InboundCallCard's Accept button. Returns the Call so the
     *  caller can wire `disconnect`/`mute`; null on failure (no device yet). */
    connectToConference: ConnectToConferenceFn;
}

const VoiceCallContext = createContext<VoiceCallContextValue | null>(null);

export function VoiceCallProvider({ children }: { children: ReactNode }) {
    const [pendingInbound, setPendingInboundState] = useState<PendingInbound | null>(null);
    const [incoming, setIncomingState] = useState<VoiceCallState["incoming"]>(null);
    const [active, setActiveState] = useState<VoiceCallState["active"]>(null);
    const [muted, setMutedState] = useState(false);
    const connectImplRef = useRef<ConnectToConferenceFn | null>(null);

    const setPendingInbound: VoiceCallContextValue["setPendingInbound"] = useCallback((v) => {
        setPendingInboundState(v);
    }, []);

    const setIncoming: VoiceCallContextValue["setIncoming"] = useCallback((v) => {
        setIncomingState(v);
    }, []);

    const setActive: VoiceCallContextValue["setActive"] = useCallback((v) => {
        setActiveState(v);
    }, []);

    const promoteToActive = useCallback(() => {
        setIncomingState((prev) => {
            if (prev) setActiveState(prev);
            return null;
        });
    }, []);

    const clearActive = useCallback(() => {
        setActiveState(null);
        setMutedState(false);
    }, []);

    const setMuted: VoiceCallContextValue["setMuted"] = useCallback((m) => {
        setMutedState(m);
    }, []);

    const patchIncomingMeta: VoiceCallContextValue["patchIncomingMeta"] = useCallback(
        (patch) => {
            setIncomingState((prev) =>
                prev ? { ...prev, meta: { ...prev.meta, ...patch } } : prev
            );
            setActiveState((prev) =>
                prev ? { ...prev, meta: { ...prev.meta, ...patch } } : prev
            );
        },
        []
    );

    const registerConnectToConference: VoiceCallContextValue["registerConnectToConference"] =
        useCallback((fn) => {
            connectImplRef.current = fn;
        }, []);

    const connectToConference: ConnectToConferenceFn = useCallback(async (name, meta) => {
        const fn = connectImplRef.current;
        if (!fn) {
            console.warn("[VoiceCallContext] connectToConference called before useVoiceDevice registered an impl");
            return null;
        }
        return fn(name, meta);
    }, []);

    const value = useMemo<VoiceCallContextValue>(
        () => ({
            pendingInbound,
            incoming,
            active,
            muted,
            setPendingInbound,
            setIncoming,
            setActive,
            promoteToActive,
            clearActive,
            setMuted,
            patchIncomingMeta,
            registerConnectToConference,
            connectToConference,
        }),
        [
            pendingInbound,
            incoming,
            active,
            muted,
            setPendingInbound,
            setIncoming,
            setActive,
            promoteToActive,
            clearActive,
            setMuted,
            patchIncomingMeta,
            registerConnectToConference,
            connectToConference,
        ],
    );

    return <VoiceCallContext.Provider value={value}>{children}</VoiceCallContext.Provider>;
}

export function useVoiceCall(): VoiceCallContextValue {
    const ctx = useContext(VoiceCallContext);
    if (!ctx) throw new Error("useVoiceCall must be used inside VoiceCallProvider");
    return ctx;
}
